if (!customElements.get('quick-add-modal')) {
  /* D5-3 (C-12, C-15, C-18): the scripts a door carries are run ONCE per page (their files define elements and bind
     document listeners; re-running them on every open cost 50 to 110 ms of WebKit and 40 live listeners a door), and one
     door opens at a time: the newest tap wins, an older pending load is dropped before it writes */
  const svntyDoorScripts = new Set();
  let svntyDoorToken = 0;
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      /* D6-16 THE QUICK LOOK DOOR v2 (R206): the page's ONE door (snippets/svnty-door-shell.liquid, #SvntyDoor) hangs in the
         header slab from the moment it upgrades and is never moved again (THE STATUS BAR law: an absolute box in the slab; the
         old door was carried into the header on every open and home on every close). Any other quick-add modal (Trade's bulk
         cards, a fallback) keeps Trade's home at the end of <body>. */
      connectedCallback() {
        if (this.moved) return;
        this.moved = true;
        const home = () => (this.id === 'SvntyDoor' && (document.querySelector('.header-wrapper .header') || document.querySelector('.header-wrapper'))) || document.body;
        home().appendChild(this);
        /* the theme editor re-renders the header section: the door goes back into the new slab */
        if (this.id === 'SvntyDoor') document.addEventListener('shopify:section:load', () => { if (!this.isConnected || !home().contains(this)) home().appendChild(this); });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.modalContent.classList.remove('svnty-door--nosize');
        this.modalContent.removeAttribute('data-svnty-door-colour');

        /* R3-10 (round 3): `preventFocus` exists in stock Dawn because the cart DRAWER opens and
           takes focus after an add. This store is `cart_type: page` — there is no drawer and no
           cart-notification — so nulling openedBy sent `removeTrapFocus()` an empty target and
           focus fell to <body>: the keyboard and AT shopper lost her place in the grid on every
           quick-add. Surrender focus only when something is actually going to take it. */
        if (preventFocus && cartNotification) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        /* R3-19: the scroll is recorded HERE, before the fetch and before the DOM is written — by the
           time ModalDialog.show() runs, the shopper has already been moved. ModalDialog.hide() puts
           her back and clears this. */
        this.svntyScrollRestore = window.scrollY || window.pageYOffset || 0;
        opener.setAttribute('aria-disabled', true);
        const spinner = opener.querySelector('.loading__spinner');
        /* D6-16: the ring shows only past 120 ms (a prefetched door opens before it, so the button never flashes) */
        const ring = setTimeout(() => {
          opener.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        }, 120);

        /* THE FAST DOOR (D4-34, v2 D6-16): the lean door section, prefetched, parsed once and cached by assets/svnty-quickadd.js
           (window.SVNTYDoor.door answers {lean, html, pic}); an open only writes the string. Trade's road (the whole page fetched,
           parsed, renamed and re-parsed on every tap) stays as the fallback when the section cannot be had. */
        const url = opener.getAttribute('data-product-url');
        const trade = () =>
          fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
              const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
              this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
              this.preventDuplicatedIDs();
              this.removeDOMElements();
              return {
                classes: [...this.productElement.classList].filter((c) => c.startsWith('color-') || c === 'gradient'),
                html: this.productElement.innerHTML,
              };
            });
        const token = ++svntyDoorToken;
        const load = window.SVNTYDoor ? window.SVNTYDoor.door(url).then((p) => p || trade()) : trade();
        const done = () => {
          clearTimeout(ring);
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        };
        load
          .then((door) => {
            if (token !== svntyDoorToken) return;   /* C-18: a newer door was asked for; this one never opens */
            document.querySelectorAll('quick-add-modal[open]').forEach((m) => { if (m !== this && m.hide) m.hide(true); });
            this.write(door);
            /* the first picture paints with the door: a prefetched one is decoded already; a cold one waits at most 60 ms (the whole
               cold open stays within the section's fetch + 80 ms) */
            const first = this.modalContent.querySelector('img[data-svnty-qd-first]');
            const open = () => {
              if (token !== svntyDoorToken) return;
              done();
              super.show(opener);
            };
            if (!first || first.complete) return open();
            return Promise.race([first.decode ? first.decode().catch(() => {}) : null, new Promise((r) => setTimeout(r, 60))]).then(open);
          })
          .catch(() => {
            /* C-19: the door could not be fetched (offline, a server error): the full page is the honest answer */
            if (token === svntyDoorToken) window.location.href = url;
          })
          .finally(() => { if (token === svntyDoorToken) done(); });
      }

      /* the write: one string; no Buy it now (the product page keeps it), no XR, no script re-run for the lean door */
      write(door) {
        const c = this.modalContent;
        c.className = 'quick-add-modal__content-info';
        door.classes.forEach((k) => c.classList.add(k));
        if (door.lean) {
          c.innerHTML = door.html;
        } else {
          this.setInnerHTML(c, door.html);
          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
        }
        const dialog = this.querySelector('[role="dialog"]');
        const title = c.querySelector('.product__title h2, .product__title h1');
        if (dialog && dialog.dataset.labelTpl && title) dialog.setAttribute('aria-label', dialog.dataset.labelTpl.replace('[name]', title.textContent.trim()));
        /* C-02: the door's own behaviour, scoped to the door (the words, the Color label, the size choice); the page's scripts
           build the door's parts on the store's own signal (svnty-buyrow.js: THE BUY ROW; svnty-quickadd-door.js: THE RAIL) */
        if (window.SVNTY && window.SVNTY.pdpMount) window.SVNTY.pdpMount(c, { noSize: true });
        document.dispatchEvent(new CustomEvent('svnty:pdp:rendered', { detail: { door: c } }));
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        /* C-12: a script file the page already runs (or an earlier door ran) is not run again */
        document.querySelectorAll('script[src]').forEach((s) => { if (!element.contains(s)) svntyDoorScripts.add(s.src); });
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          if (oldScriptTag.src) {
            if (svntyDoorScripts.has(oldScriptTag.src)) { oldScriptTag.remove(); return; }
            svntyDoorScripts.add(oldScriptTag.src);
          }
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-selects');
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        /* D4-34: the viewer removes itself inside a door (svnty-viewer.js), so its every-picture markup never enters it */
        this.productElement.querySelectorAll('svnty-viewer').forEach((viewer) => viewer.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        /* C-16 (D5-3): THE DOOR's own geometry (svnty-quickadd.css section 5): from 750 px the picture column is 1.15 / 2.15 of
           a card min(1040px, 100vw - 48px) less its 24 px gutters; on the phone the whole width */
        const svntyImgs = this.modalContent.querySelectorAll('.product__media img');
        if (svntyImgs.length) {
          svntyImgs.forEach((img) => img.setAttribute('sizes', '(min-width: 750px) calc(min(1040px, 100vw - 48px) * 0.535 - 48px), 100vw'));
          return;
        }
        const product = this.modalContent.querySelector('.product');
        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
